import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../../../../shared/lib/supabase'
import { useAuth } from '../../../../../contexts/AuthContext'
import { logAuditEvent } from '../../../../../shared/lib/auditLog'

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'policy', label: 'Policy' },
  { value: 'report', label: 'Report' },
  { value: 'contract', label: 'Contract' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'cyber_insurance', label: 'Cyber Insurance' }
]

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'image/png',
  'image/jpeg'
]

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB

function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType) {
  if (mimeType?.includes('pdf')) return 'text-red-500'
  if (mimeType?.includes('word') || mimeType?.includes('document')) return 'text-blue-500'
  if (mimeType?.includes('sheet') || mimeType?.includes('excel') || mimeType?.includes('csv')) return 'text-green-500'
  if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return 'text-orange-500'
  if (mimeType?.includes('image')) return 'text-purple-500'
  return 'text-gray-400'
}

function DocumentsTab({ client }) {
  const { user, profile } = useAuth()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [uploadCategory, setUploadCategory] = useState('general')
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const fileInputRef = useRef(null)

  const clientId = client?.id

  const fetchDocuments = useCallback(async () => {
    if (!clientId) return
    try {
      let query = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })

      if (categoryFilter) query = query.eq('category', categoryFilter)

      const { data, error: fetchErr } = await query
      if (fetchErr) throw fetchErr
      setDocuments(data || [])
    } catch (err) {
      console.error('Failed to fetch documents:', err)
    } finally {
      setLoading(false)
    }
  }, [clientId, categoryFilter])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const handleUpload = async (files) => {
    if (!files || files.length === 0 || !clientId) return
    setError('')
    setUploading(true)

    try {
      for (const file of files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          setError(`${file.name}: File type not supported. Upload PDF, DOCX, XLSX, PPTX, CSV, TXT, PNG, or JPG files.`)
          continue
        }
        if (file.size > MAX_FILE_SIZE) {
          setError(`${file.name}: File size must be under 25MB.`)
          continue
        }

        const fileId = crypto.randomUUID()
        const filePath = `${clientId}/${fileId}-${file.name}`

        const { error: uploadErr } = await supabase.storage
          .from('client-documents')
          .upload(filePath, file, { contentType: file.type, upsert: false })

        if (uploadErr) throw uploadErr

        const { error: dbErr } = await supabase
          .from('client_documents')
          .insert({
            client_id: clientId,
            file_name: file.name,
            file_path: filePath,
            file_size: file.size,
            mime_type: file.type,
            category: uploadCategory,
            uploaded_by: user.id,
            uploaded_by_role: profile?.role === 'client' ? 'client' : 'admin'
          })

        if (dbErr) throw dbErr

        logAuditEvent({
          category: 'change',
          action: 'document.uploaded',
          description: `Uploaded document: ${file.name}`,
          clientId,
          clientName: client?.name,
          resourceType: 'document',
          metadata: { fileName: file.name, fileSize: file.size, docCategory: uploadCategory }
        })
      }

      await fetchDocuments()
    } catch (err) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDownload = async (doc) => {
    try {
      const { data, error: dlErr } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60)

      if (dlErr) throw dlErr
      window.open(data.signedUrl, '_blank')
    } catch (err) {
      setError('Download failed: ' + err.message)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    setDeleting(true)
    try {
      await supabase.storage.from('client-documents').remove([deleteConfirm.file_path])
      await supabase.from('client_documents').delete().eq('id', deleteConfirm.id)

      logAuditEvent({
        category: 'change',
        action: 'document.deleted',
        description: `Deleted document: ${deleteConfirm.file_name}`,
        clientId,
        clientName: client?.name,
        resourceType: 'document'
      })

      setDeleteConfirm(null)
      fetchDocuments()
    } catch (err) {
      setError('Delete failed: ' + err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleUpload(e.dataTransfer.files)
  }

  const categoryCounts = documents.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-4">
      {/* Upload area with integrated category selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Category selector + filter row */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Upload to:</label>
            <div className="flex gap-1">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setUploadCategory(c.value)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
                    uploadCategory === c.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">Filter:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All ({documents.length})</option>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label} ({categoryCounts[c.value] || 0})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Drop zone */}
        <div
          className={`p-6 text-center transition-colors cursor-pointer ${
            dragOver ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg"
            onChange={(e) => handleUpload(e.target.files)}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-3 py-2">
              <span className="animate-spin inline-block w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full" />
              <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">Uploading...</span>
            </div>
          ) : (
            <>
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-primary-600 dark:text-primary-400">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PDF, DOCX, XLSX, PPTX, CSV, TXT, PNG, JPG (max 25MB)</p>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">Dismiss</button>
        </div>
      )}

      {/* Documents table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : documents.length === 0 ? (
          <div className="py-12 text-center">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400">No documents uploaded yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Upload files using the area above</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uploaded By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {documents.map(doc => (
                <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <button onClick={() => handleDownload(doc)} className="flex items-center gap-2 text-sm text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-left">
                      <svg className={`w-4 h-4 flex-shrink-0 ${getFileIcon(doc.mime_type)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="truncate max-w-xs">{doc.file_name}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 capitalize">
                      {doc.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${doc.uploaded_by_role === 'client' ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {doc.uploaded_by_role === 'client' ? 'Client' : 'Admin'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(doc.file_size)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDeleteConfirm(doc)}
                      className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      title="Delete document"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Delete Document</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">{deleteConfirm.file_name}</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentsTab
